import { useRef, useState, forwardRef, useImperativeHandle } from 'react'
import TeethDiagramInterractive from "../../TeethDiagram/TeethDiagramInterractive";
import DentalServiceForm from "./DentalServiceForm";
import { useEffect } from 'react';
import axiosInstance from "../../../api/axiosInstance";
import { getTeethNos, toothLabel, isPrimaryToothId } from '../../../utils/functions';
import moment from 'moment';

const getCurrentUserName = () => {
    try {
        const raw = localStorage.getItem("userMedical");
        if (!raw) return "Unknown";
        const d = JSON.parse(raw);
        const u = d?.data || d;
        return [u?.prefix, u?.fullName || u?.name].filter(Boolean).join(" ") || "Unknown";
    } catch { return "Unknown"; }
};

// Children are charted on the primary dentition until the permanent teeth are
// essentially in; the first permanent molars and incisors erupt at around 6, so
// 7 is a safe default for opening on the adult chart. It is only a default —
// the clinician switches freely.
const PRIMARY_DENTITION_MAX_AGE = 7;

const DentalDiagram = forwardRef((props, ref) => {
    const [teethChartMode, setTeethChartMode] = useState(null);
    const [selectedTeeth, setSelectedTeeth] = useState([]);
    const [dentition, setDentition] = useState(null);
    const formRef = useRef(null)

    // Add Notes modal state
    const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
    const [noteText, setNoteText] = useState("");
    const [noteSaving, setNoteSaving] = useState(false);
    const [noteTemplates, setNoteTemplates] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);

    useImperativeHandle(ref, () => ({
        showEditForm(d) {
            if (!formRef.current)
                return;
            formRef.current.setTheServiceFormData(d)
            formRef.current.setTheServiceSearchQuery("")
            formRef.current.toggleModal()
        }
    }));

    const handleDiagramClick = (info) => {
        if (teethChartMode !== null && teethChartMode != info.type) {
            return;
        }

        setTeethChartMode(info.type)
        setSelectedTeeth((prev) => {
            if (!prev) {
                return [info]
            }
            let exist = prev.findIndex(t => (t.type + t.id + t.symbol) === (info.type + info.id + info.symbol));
            if (exist === -1) {
                return [...prev, info]
            } else {
                if (prev && prev.length === 1) {
                    setTeethChartMode(null)
                }
                return prev.filter((_, index) => index !== exist)
            }
        })
    }

    const resetSelection = () => {
        setSelectedTeeth([])
        setTeethChartMode(null)
    }

    // Seed the chart from the patient's age once it is known, then leave it to
    // the clinician. patientAge may arrive after the first render.
    useEffect(() => {
        if (dentition !== null)
            return

        const age = props.patientAge
        if (age === undefined || age === null || isNaN(age))
            return

        setDentition(age < PRIMARY_DENTITION_MAX_AGE ? "primary" : "permanent")
    }, [props.patientAge, dentition])

    const activeDentition = dentition ?? "permanent"

    const switchDentition = (next) => {
        if (next === activeDentition)
            return
        // A selection refers to teeth on one chart only.
        resetSelection()
        setDentition(next)
    }

    // Procedures recorded on the chart that is not on screen would otherwise look
    // lost: primary and permanent ids are disjoint, so the chart silently ignores
    // anything that is not its own.
    const otherChartCount = (() => {
        if (!Array.isArray(props.existingData))
            return 0
        const wantPrimary = activeDentition !== "primary"
        return new Set(
            props.existingData
                .filter(e => e && e.id !== undefined && isPrimaryToothId(e.id) === wantPrimary)
                .map(e => e.id)
        ).size
    })()

    const onAddedSuccessfully = () => {
        if (props.onAddedSuccessfully) {
            props.onAddedSuccessfully()
        }
        resetSelection()
    }

    const dentalChartRef = useRef(null)
    const [dentalChartHeight, setDentalChartHeight] = useState(-1)

    useEffect(() => {
        if (dentalChartHeight !== -1)
            return

        if (!dentalChartRef.current?.offsetHeight)
            return


        setDentalChartHeight(dentalChartRef.current?.offsetHeight)
    }, [selectedTeeth])

    useEffect(() => {
        const handleResize = (event) => {
            if (dentalChartRef.current?.offsetHeight)
                setDentalChartHeight(dentalChartRef.current?.offsetHeight)
        }
        window.addEventListener('resize', handleResize, true);
        return () => window.removeEventListener('resize', handleResize);
    }, [])

    const handleAddService = () => {
        if (formRef.current) {
            formRef.current.setTheServiceFormData({
                serviceId: "",
                extra_notes: "",
                discountType: "Amount",
                discountValue: 0,
                status: "Plan",
                details: []
            })
            formRef.current.setTheServiceSearchQuery("")
            formRef.current.toggleModal()
        }
    }

    const handleOpenNotesModal = async () => {
        setNoteText("");
        setSuggestions([]);
        setShowSuggestions(false);
        setIsNotesModalOpen(true);
        try {
            const res = await axiosInstance.get("getDentalNoteTemplates");
            if (res.data?.success) setNoteTemplates(res.data.data || []);
        } catch (e) { /* silent */ }
    }

    const handleCloseNotesModal = () => {
        setNoteText("");
        setSuggestions([]);
        setShowSuggestions(false);
        setIsNotesModalOpen(false);
    }

    const handleNoteTextChange = (value) => {
        setNoteText(value);
        if (value.trim().length >= 2) {
            const lower = value.toLowerCase();
            const filtered = noteTemplates.filter(t => t.toLowerCase().includes(lower));
            setSuggestions(filtered.slice(0, 8));
            setShowSuggestions(filtered.length > 0);
        } else {
            setSuggestions([]);
            setShowSuggestions(false);
        }
    }

    const handleSuggestionClick = (text) => {
        setNoteText(text);
        setSuggestions([]);
        setShowSuggestions(false);
    }

    const handleSubmitNote = async () => {
        if (!noteText.trim()) return;
        setNoteSaving(true);
        try {
            const text = noteText.trim();
            const res = await axiosInstance.post("addTeethNote", {
                patient_id: props.patientId,
                teeth_ids: selectedTeeth.map(({ type, id, symbol }) => ({ type, id, symbol })),
                note_text: text,
            });
            if (res.data?.success) {
                // Save as template (fire-and-forget)
                axiosInstance.post("saveDentalNoteTemplate", { note_text: text }).catch(() => {});
                setIsNotesModalOpen(false);
                setNoteText("");
                setSuggestions([]);
                setShowSuggestions(false);
                if (props.onNoteAdded) props.onNoteAdded();
            }
        } catch (e) {
            console.error(e);
        } finally {
            setNoteSaving(false);
        }
    }

    return (<>
        <div className="row">
            <div className="col-8">
                <div ref={dentalChartRef}>
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                        <h6 className="m-0">Dental Chart (Click on Teeth to Select Multiple)</h6>
                        <div className="d-flex align-items-center gap-2">
                            {otherChartCount > 0 && (
                                <span className="small text-muted">
                                    <i className="fas fa-circle-info me-1"></i>
                                    {otherChartCount} tooth{otherChartCount === 1 ? "" : "s"} charted on the{" "}
                                    {activeDentition === "primary" ? "adult" : "child"} chart
                                </span>
                            )}
                            <div className="btn-group btn-group-sm" role="group" aria-label="Dentition">
                                <button
                                    type="button"
                                    className={"btn " + (activeDentition === "permanent" ? "btn-secondary" : "btn-outline-secondary")}
                                    style={{ fontSize: 12, padding: "4px 10px" }}
                                    onClick={() => switchDentition("permanent")}
                                >
                                    Adult (Permanent)
                                </button>
                                <button
                                    type="button"
                                    className={"btn " + (activeDentition === "primary" ? "btn-secondary" : "btn-outline-secondary")}
                                    style={{ fontSize: 12, padding: "4px 10px" }}
                                    onClick={() => switchDentition("primary")}
                                >
                                    Child (Primary)
                                </button>
                            </div>
                        </div>
                    </div>
                    {teethChartMode && <div class="alert alert-info small" role="alert">
                        <button className="bgBtn btn-sm" style={{ fontSize: 13, padding: "5px 12px" }} onClick={resetSelection}>Clear Selection</button>
                        &nbsp;
                        Selection Mode is {props.teethTypeDescription[teethChartMode]}
                    </div>}
                    <div className='teethDiagramContainer'>
                        <TeethDiagramInterractive
                            isFdi={props.isFdi}
                            dentition={activeDentition}
                            existingData={props.existingData}
                            selected={selectedTeeth}
                            onClick={handleDiagramClick}
                            onMouseMove={props.onDiagramMouseMove}
                            onMouseLeave={props.onDiagramMouseMove}
                        />
                    </div>
                </div>
            </div>
            <div className="col-4">
                <div class="alert alert-light d-flex align-items-center justify-content-center" role="alert"
                    style={{ height: (dentalChartHeight === -1) ? "100%" : (dentalChartHeight + "px"), overflowY: "auto" }}>
                    <div class="h-100 w-100" style={{ flexGrow: 2 }}>
                        <div className="d-flex justify-content-between align-items-center">
                            <h6 className="m-0">Selected Teeth</h6>
                            <div className="d-flex gap-1">
                                <button
                                    type="button"
                                    className="bgBtn btn-sm d-flex align-items-center gap-1"
                                    style={{ fontSize: 13, padding: "5px 12px" }}
                                    onClick={handleAddService}
                                >
                                    Add Service
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                                    style={{ fontSize: 13, padding: "5px 12px" }}
                                    onClick={handleOpenNotesModal}
                                >
                                    Add Notes
                                </button>
                            </div>
                        </div>
                        <hr />
                        {(selectedTeeth && selectedTeeth.length > 0) ? (
                            <ul className="list-group list-group-flush">
                                {selectedTeeth.map((info, index) => {
                                    return (
                                        <li className='list-group-item small' key={index}>
                                            <b>{toothLabel(info.id, props.isFdi)}:</b> ({props.teethTypeDescription[info.type]}) {info.description}
                                        </li>
                                    );
                                })}
                            </ul>
                        ) : (
                            <span className='small text-muted'>Select Teeth</span>
                        )}
                        <hr className="my-2" />
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="m-0 d-flex align-items-center gap-1" style={{ fontSize: "13px", color: "#2a5478" }}>
                                <i className="fas fa-tooth"></i> Teeth Notes
                            </h6>
                        </div>
                        {props.teethNotesLoading ? (
                            <span className="small text-muted">Loading...</span>
                        ) : props.teethNotes?.length > 0 ? (
                            <div className="d-flex flex-column gap-2">
                                {props.teethNotes.map((n, i) => {
                                    let parsedTeeth = [];
                                    try { parsedTeeth = JSON.parse(n.teeth_ids || "[]"); } catch (e) {}
                                    const teethNos = getTeethNos(parsedTeeth, props.isFdi);
                                    return (
                                        <div key={i}
                                            onClick={() => props.onViewTeethNote && props.onViewTeethNote(n)}
                                            style={{
                                                cursor: "pointer",
                                                background: "#2a547808",
                                                border: "1px solid #2a547825",
                                                borderLeft: "3px solid #2a5478",
                                                borderRadius: "6px",
                                                padding: "6px 8px",
                                            }}
                                        >
                                            <div className="d-flex align-items-center gap-1 mb-1" style={{ fontSize: "11px" }}>
                                                <span style={{ color: "#2a5478", fontWeight: 600 }}>
                                                    <i className="fas fa-clock me-1"></i>
                                                    {moment(n.created_at).format("DD/MM/YYYY HH:mm")}
                                                </span>
                                                {teethNos.length > 0 && (
                                                    <span style={{ background: "#2a5478", color: "#fff", borderRadius: "4px", padding: "1px 5px", fontSize: "10px" }}>
                                                        <i className="fas fa-tooth me-1"></i>{teethNos.join(", ")}
                                                    </span>
                                                )}
                                            </div>
                                            <div style={{ fontSize: "11px", color: "#444", whiteSpace: "pre-wrap", lineHeight: 1.4 }}>
                                                {n.note_text?.length > 80 ? n.note_text.slice(0, 80) + "…" : n.note_text}
                                            </div>
                                            <div style={{ fontSize: "10px", color: "#888", marginTop: "3px" }}>
                                                <i className="fas fa-user-md me-1"></i>{n.doctor_name?.trim() || "Unknown"}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <span className="small text-muted">No teeth notes yet</span>
                        )}
                    </div></div>
            </div>
        </div >
        <DentalServiceForm isFdi={props.isFdi} onAddedSuccessfully={onAddedSuccessfully} teethChartMode={teethChartMode} ref={formRef} selectedTeeth={selectedTeeth} patientId={props.patientId} />

        {/* Add Notes Modal */}
        {isNotesModalOpen && (() => {
            const teethNos = getTeethNos(selectedTeeth, props.isFdi);
            const currentUser = getCurrentUserName();
            const now = moment().format("DD/MM/YYYY  HH:mm");
            return (
                <div className="modal fade show" style={{ backgroundColor: "rgba(20,40,60,0.55)", display: "block" }} onClick={handleCloseNotesModal}>
                    <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
                        <div className="modal-content" style={{ borderRadius: "12px", overflow: "hidden", border: "none", boxShadow: "0 8px 32px rgba(42,84,120,0.18)" }}>
                            {/* Colored header */}
                            <div style={{ background: "linear-gradient(135deg, #2a5478 0%, #3a7ca5 100%)", padding: "18px 20px", color: "#fff" }}>
                                <div className="d-flex align-items-center justify-content-between">
                                    <div className="d-flex align-items-center gap-2">
                                        <div style={{ background: "rgba(255,255,255,0.18)", borderRadius: "8px", width: 38, height: 38, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px" }}>
                                            <i className="fas fa-tooth"></i>
                                        </div>
                                        <div>
                                            <div style={{ fontWeight: 700, fontSize: "15px" }}>Add Teeth Note</div>
                                            {teethNos.length > 0 && (
                                                <div style={{ fontSize: "11px", opacity: 0.85 }}>
                                                    <i className="fas fa-tooth me-1"></i>{teethNos.join(", ")}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <button type="button" className="btn-close btn-close-white" onClick={handleCloseNotesModal} />
                                </div>
                                <div className="d-flex align-items-center gap-3 mt-3" style={{ fontSize: "12px", opacity: 0.9 }}>
                                    <span><i className="fas fa-user-md me-1"></i>{currentUser}</span>
                                    <span><i className="fas fa-clock me-1"></i>{now}</span>
                                </div>
                            </div>
                            {/* Body */}
                            <div className="modal-body" style={{ padding: "20px" }}>
                                {teethNos.length === 0 && (
                                    <div className="d-flex align-items-center gap-2 mb-3 p-2" style={{ background: "#fff8e1", border: "1px solid #ffe082", borderRadius: "6px", fontSize: "12px", color: "#7a6000" }}>
                                        <i className="fas fa-info-circle"></i>
                                        No teeth selected — note will apply to the patient generally.
                                    </div>
                                )}
                                <label className="form-label" style={{ fontWeight: 600, color: "#2a5478", fontSize: "13px" }}>
                                    <i className="fas fa-pen me-1"></i>Note
                                </label>
                                <div style={{ position: "relative" }}>
                                    <textarea
                                        className="form-control"
                                        rows={5}
                                        placeholder="Enter clinical note..."
                                        value={noteText}
                                        onChange={e => handleNoteTextChange(e.target.value)}
                                        onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
                                        onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                                        style={{ borderColor: "#c8dcea", borderRadius: "8px", fontSize: "14px", resize: "vertical" }}
                                        autoFocus
                                    />
                                    {showSuggestions && suggestions.length > 0 && (
                                        <div style={{
                                            position: "absolute", top: "100%", left: 0, right: 0, zIndex: 9999,
                                            background: "#fff", border: "1px solid #c8dcea", borderRadius: "0 0 8px 8px",
                                            boxShadow: "0 4px 12px rgba(42,84,120,0.12)", maxHeight: 200, overflowY: "auto",
                                        }}>
                                            {suggestions.map((s, i) => (
                                                <div key={i}
                                                    onMouseDown={() => handleSuggestionClick(s)}
                                                    style={{
                                                        padding: "8px 12px", fontSize: "13px", cursor: "pointer",
                                                        color: "#2c3e50", borderBottom: i < suggestions.length - 1 ? "1px solid #f0f4f8" : "none",
                                                        whiteSpace: "pre-wrap", wordBreak: "break-word",
                                                    }}
                                                    onMouseEnter={e => e.currentTarget.style.background = "#f0f6fb"}
                                                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                                                >
                                                    <i className="fas fa-history me-2" style={{ color: "#2a5478", fontSize: 11 }} />
                                                    {s.length > 100 ? s.slice(0, 100) + "…" : s}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="modal-footer" style={{ borderTop: "1px solid #e8f0f7", padding: "10px 20px" }}>
                                <button type="button" className="btn btn-outline-secondary btn-sm" style={{ borderRadius: "6px" }} onClick={handleCloseNotesModal}>Cancel</button>
                                <button
                                    type="button"
                                    className="bgBtn btn-sm"
                                    style={{ padding: "6px 20px", borderRadius: "6px" }}
                                    onClick={handleSubmitNote}
                                    disabled={noteSaving || !noteText.trim()}
                                >
                                    {noteSaving ? <><i className="fas fa-spinner fa-spin me-1"></i>Saving…</> : <><i className="fas fa-save me-1"></i>Save Note</>}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            );
        })()}
    </>)
})
export default DentalDiagram
